import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Phone, ArrowRight, Wrench, Car, Settings, Hammer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const WorkshopEPC = () => {
  const faqItems = [
    {
      question: 'Do workshops require an EPC?',
      answer: 'Yes, all workshops in England require an Energy Performance Certificate when being sold, let or constructed. This includes vehicle repair workshops, joinery workshops, engineering workshops and any light industrial premises over 50m² that use energy for heating or lighting.'
    },
    {
      question: 'What is a typical EPC rating for a workshop?',
      answer: 'Workshops typically achieve ratings between C and F depending on age, construction and heating systems. Modern insulated units with LED lighting often achieve C or D, while older concrete or metal-clad workshops with poor insulation commonly rate E or F.'
    },
    {
      question: 'How long is a workshop EPC valid?',
      answer: 'A workshop EPC is valid for 10 years from the date of issue. However, if you make significant improvements to the building (such as new heating systems or insulation), you may want to obtain a new EPC to reflect the improved rating.'
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Workshop EPC Assessments | Light Industrial Unit EPCs | South West UK</title>
        <meta name="description" content="Professional EPC assessments for workshops and light industrial units across Devon, Cornwall, Somerset and Dorset. Trade workshops, vehicle repair and engineering premises." />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/workshop-epc" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      
      <Navbar />
      <Breadcrumbs items={[{ label: 'Industrial EPC', path: '/industrial-epc' }, { label: 'Workshop EPC', path: '/workshop-epc' }]} />
      
      <section className="bg-gradient-to-br from-amber-800 via-amber-700 to-amber-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-200 px-4 py-2 rounded-full text-sm mb-6">
              <Wrench className="w-4 h-4" />
              Workshop EPC Specialists
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Workshop EPC Assessments</h1>
            <p className="text-xl text-amber-200 mb-4">Trade Workshops • Vehicle Repair • Engineering Units • Joinery Shops</p>
            <p className="text-lg text-amber-300 mb-8 max-w-3xl mx-auto">
              Expert Energy Performance Certificates for workshops and light industrial units across Devon, Cornwall, Somerset and Dorset. Our accredited assessors understand the specific requirements of trade premises.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
                <Link to="/booking?service=commercial_warehouse">Get Workshop EPC Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077"><Phone className="mr-2 w-5 h-5" />Call 01392 980077</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Energy Performance Certificates for Workshops</h2>
            <p className="text-gray-700">
              Workshops and light industrial units form the backbone of the South West's trade economy. From vehicle repair garages and MOT testing stations to joinery workshops, engineering premises and fabrication units, these buildings require Energy Performance Certificates when being sold, let or constructed.
            </p>
            <p className="text-gray-700">
              Our accredited non-domestic energy assessors specialise in workshop EPC surveys across Devon, Cornwall, Somerset and Dorset. We understand that workshops often combine working areas with office space, trade counters or parts storage, requiring careful assessment of different zones within the same building.
            </p>
            <p className="text-gray-700">
              Workshop EPCs are calculated using SBEM (Simplified Building Energy Model) methodology, examining the building's construction, heating systems, lighting, ventilation and any renewable energy installations. The resulting certificate rates the building from A (most efficient) to G (least efficient) and provides recommendations for improvement.
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Workshop Types We Assess</h2>
            <div className="grid md:grid-cols-2 gap-4 not-prose">
              {[
                { icon: Car, title: 'Vehicle Workshops', desc: 'MOT stations, repair garages, bodyshops and valeting centres' },
                { icon: Hammer, title: 'Trade Workshops', desc: 'Joinery, carpentry, plumbing and electrical contractors' },
                { icon: Settings, title: 'Engineering Units', desc: 'Fabrication, machining and precision engineering' },
                { icon: Wrench, title: 'Service Centres', desc: 'Plant hire depots, equipment maintenance facilities' }
              ].map((item) => (
                <Card key={item.title}>
                  <CardContent className="p-4 flex items-start gap-4">
                    <item.icon className="w-8 h-8 text-amber-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Factors Affecting Workshop EPC Ratings</h2>
            <p className="text-gray-700">
              Several factors influence workshop energy performance. Our assessors examine:
            </p>
            <ul className="text-gray-700">
              <li><strong>Heating Systems:</strong> Overhead radiant heaters, warm air blowers, unit heaters and workshop-specific heating solutions</li>
              <li><strong>Lighting:</strong> High-level fluorescent or LED lighting, task lighting and natural daylight provision</li>
              <li><strong>Building Fabric:</strong> Wall insulation, roof construction, roller shutter doors and glazing</li>
              <li><strong>Ventilation:</strong> Extraction systems for fumes, dust or spray painting, general ventilation requirements</li>
              <li><strong>Office/Ancillary Areas:</strong> Separate heating and lighting for office, reception or staff areas</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Workshop EPC FAQs</h2>
            <div className="space-y-6">
              {faqItems.map((faq) => (
                <div key={faq.question} className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/industrial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <h3 className="font-semibold">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">All industrial building types</p>
              </Link>
              <Link to="/factory-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <h3 className="font-semibold">Factory EPCs</h3>
                <p className="text-sm text-gray-600">Manufacturing facilities</p>
              </Link>
              <Link to="/warehouse-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                <h3 className="font-semibold">Warehouse EPCs</h3>
                <p className="text-sm text-gray-600">Distribution centres</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Get Your Workshop EPC Quote</h2>
          <p className="text-xl text-green-100 mb-8">Fast, professional assessments across Devon, Cornwall, Somerset and Dorset.</p>
          <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
            <Link to="/booking?service=commercial_warehouse">Request Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default WorkshopEPC;